import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const DEFAULT_HUBSPOT_OAUTH_CALLBACK_URL =
  'https://app.chattitude.ai/slickbots-ws/api/integrations/oauth/callback';

/** HubSpot redirect URL: reseller origin + slickbots-ws API OAuth callback path (matches deployed API base). */
function hubSpotOAuthCallbackDisplayUrl(resellerBaseUrl) {
  const t = (resellerBaseUrl || '').trim().replace(/\/+$/, '');
  if (!t) return DEFAULT_HUBSPOT_OAUTH_CALLBACK_URL;
  return `${t}/slickbots-ws/api/integrations/oauth/callback`;
}

const HubSpotModal = ({ open, onClose, productName = 'Chattitude', resellerBaseUrl }) => {
  const hubspotRedirectUrl = useMemo(
    () => hubSpotOAuthCallbackDisplayUrl(resellerBaseUrl),
    [resellerBaseUrl]
  );
  const [credentials, setCredentials] = useState({ clientId: '', clientSecret: '' });
  const [pipelineConfig, setPipelineConfig] = useState({ ticketPipelineId: '', ticketPipelineStageId: '' });
  const [expandedAccordion, setExpandedAccordion] = useState('instructions');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPipelineConfig, setLoadingPipelineConfig] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  const authHeader = () => {
    const jwtToken = Cookies.getJwtToken();
    if (jwtToken) return { Authorization: `Bearer ${jwtToken}` };
    // Fallback: backend supports direct tenant-id in Authorization for testing
    const tenantId = Cookies.getTenantId();
    return tenantId ? { Authorization: String(tenantId) } : {};
  };

  useEffect(() => {
    const fetchCredentials = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/integrations/hubspot/credentials`, { headers: authHeader() });
        setCredentials({
          clientId: res.data?.clientId || '',
          clientSecret: res.data?.clientSecret || '',
        });
      } catch (e) {
        setCredentials({ clientId: '', clientSecret: '' });
        setError('Failed to fetch credentials.');
      } finally {
        setLoading(false);
      }
    };

    const fetchPipelineConfig = async () => {
      setLoadingPipelineConfig(true);
      try {
        const res = await axios.get(`${API_URL}/integrations/hubspot/settings`, { headers: authHeader() });
        setPipelineConfig({
          ticketPipelineId: res.data?.ticketPipelineId || '',
          ticketPipelineStageId: res.data?.ticketPipelineStageId || '',
        });
      } catch (e) {
        setPipelineConfig({ ticketPipelineId: '', ticketPipelineStageId: '' });
      } finally {
        setLoadingPipelineConfig(false);
      }
    };

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/integrations/hubspot/status`, { headers: authHeader() });
        setConnected(!!res.data?.connected);
      } catch (e) {
        setConnected(false);
      }
    };

    if (open) {
      fetchCredentials();
      fetchPipelineConfig();
      fetchStatus();
      setExpandedAccordion('instructions');
      setSnackbar({ open: false, message: '', severity: 'success' });
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const handlePipelineConfigChange = (e) => {
    const { name, value } = e.target;
    setPipelineConfig((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!credentials.clientId) return 'Client ID is required.';
    if (!credentials.clientSecret) return 'Client Secret is required.';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/hubspot/credentials`, credentials, { headers: authHeader() });
      // Also persist pipeline defaults if user edited them
      await axios.post(`${API_URL}/integrations/hubspot/settings`, pipelineConfig, { headers: authHeader() });
      setSnackbar({ open: true, message: 'HubSpot settings saved successfully!', severity: 'success' });
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save HubSpot settings.', severity: 'error' });
      setError('Failed to save HubSpot settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePipelineConfig = async () => {
    setLoadingPipelineConfig(true);
    try {
      await axios.post(`${API_URL}/integrations/hubspot/settings`, pipelineConfig, { headers: authHeader() });
      setSnackbar({ open: true, message: 'Pipeline configuration saved.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save pipeline configuration.', severity: 'error' });
    } finally {
      setLoadingPipelineConfig(false);
    }
  };

  const handleConnectHubSpot = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      // Save creds first
      await axios.post(`${API_URL}/integrations/hubspot/credentials`, credentials, { headers: authHeader() });

      const res = await axios.get(`${API_URL}/integrations/hubspot/authorize-url`, { headers: authHeader() });
      const url = res.data?.url;
      if (!url) throw new Error('Missing authorize URL');

      window.open(url, '_blank');

      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_URL}/integrations/hubspot/status`, { headers: authHeader() });
          if (statusRes.data?.connected) {
            setConnected(true);
            setConnecting(false);
            clearInterval(poll);
            setSnackbar({ open: true, message: 'HubSpot connected!', severity: 'success' });
          }
        } catch (e) {
          // ignore while polling
        }
      }, 2000);
      setPollInterval(poll);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to initiate HubSpot OAuth.', severity: 'error' });
      setError('Failed to initiate HubSpot OAuth.');
      setConnecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{ style: { minWidth: 1200, minHeight: 800, zIndex: 99999 } }}
    >
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        HubSpot Credentials
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Client ID"
              name="clientId"
              value={credentials.clientId}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Client Secret"
              name="clientSecret"
              value={credentials.clientSecret}
              onChange={handleChange}
              required
              fullWidth
            />

            <Accordion
              expanded={expandedAccordion === 'pipeline'}
              onChange={() => setExpandedAccordion((v) => (v === 'pipeline' ? false : 'pipeline'))}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Pipeline Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
                  These values control the default HubSpot ticket pipeline and stage used when creating tickets for this tenant.
                  They’re usually auto-detected after connecting HubSpot, but you can override them here if needed.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Ticket Pipeline ID"
                    name="ticketPipelineId"
                    value={pipelineConfig.ticketPipelineId}
                    onChange={handlePipelineConfigChange}
                    fullWidth
                    placeholder={loadingPipelineConfig ? 'Loading…' : 'e.g. 0'}
                    disabled={loadingPipelineConfig}
                  />
                  <TextField
                    label="Ticket Pipeline Stage ID"
                    name="ticketPipelineStageId"
                    value={pipelineConfig.ticketPipelineStageId}
                    onChange={handlePipelineConfigChange}
                    fullWidth
                    placeholder={loadingPipelineConfig ? 'Loading…' : 'e.g. 2879799000'}
                    disabled={loadingPipelineConfig}
                  />

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      onClick={handleSavePipelineConfig}
                      variant="outlined"
                      color="primary"
                      disabled={loadingPipelineConfig}
                    >
                      Save Pipeline Configuration
                    </Button>
                    {loadingPipelineConfig && <CircularProgress size={18} />}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expandedAccordion === 'instructions'}
              onChange={() => setExpandedAccordion((v) => (v === 'instructions' ? false : 'instructions'))}
              sx={{ mt: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Hubspot Integration Instructions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  For the Hubspot integration you will need access to your Developer Dashboard and then you will need two items. First you will need your Hubspot Client ID and second your Client Secret. Below are the steps you will need to take:
                </Typography>
                <ol style={{ paddingLeft: 18, marginBottom: 8 }}>
                  <li>Navigate to your Developer Dashboard Overview.</li>
                  <li>On the left navigation panel, choose the &quot;Legacy Apps&quot; option.</li>
                  <li>
                    At the top right of the screen, click the orange &quot;Create legacy app&quot; button which will open a dialog box where you want to choose the &quot;Public&quot; option.
                  </li>
                  <li>
                    On the App Info tab, enter the following information:
                    <ul style={{ marginTop: 4 }}>
                      <li>{`Public app name - ${productName}`}</li>
                      <li>{`Description - ${productName} access for ticket management`}</li>
                    </ul>
                  </li>
                  <li>
                    Switch to the Auth tab and enter the below information:
                    <ul style={{ marginTop: 4 }}>
                      <li>{`Redirect URLs - ${hubspotRedirectUrl}`}</li>
                    </ul>
                  </li>
                  <li>
                    Click the &quot;+ Add new scope&quot; button and check the boxes for the following scopes:
                    <Typography component="span" variant="body2" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: '#555' }}>
                      NOTE: When adding the below scopes, you can copy these one at a time in the search bar to find them easier. For each one after checking the box, keep the default &quot;Required&quot; option from the drop down. Once you have added all four, click the orange &quot;Update&quot; button at the bottom.
                    </Typography>
                    <ul style={{ marginTop: 4 }}>
                      <li>crm.objects.custom.read</li>
                      <li>crm.objects.custom.write</li>
                      <li>oauth (This one should be checked by default)</li>
                      <li>tickets</li>
                      <li>crm.objects.contacts.read</li>
                      <li>crm.objects.contacts.write</li>
                    </ul>
                  </li>
                  <li>
                    Click the orange &quot;Create App&quot; button at the bottom of the screen which will then populate the Auth Settings at the top of the page. From there you can copy your Client ID and Client Secret key and paste them in the above form fields
                  </li>
                  <li>Once you have entered both your Client ID and Client Secret key, click the &quot;Save&quot; button below to activate your integration.</li>
                </ol>
              </AccordionDetails>
            </Accordion>

            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
        <Button onClick={handleSave} variant="outlined" color="primary" disabled={loading || connecting} sx={{ minWidth: 180, py: 1.2 }}>
          SAVE &amp; Close
        </Button>
        <Button
          onClick={handleConnectHubSpot}
          variant="contained"
          color="success"
          disabled={connecting || loading || !credentials.clientId || !credentials.clientSecret}
          sx={{ minWidth: 220, py: 1.2 }}
        >
          {connected ? 'Connected to HubSpot' : connecting ? 'Connecting...' : 'Connect to HubSpot'}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default HubSpotModal;

